import { FC, ReactNode, useState } from "react"

import { ReactComponent as SvgInvoice } from "@brand_assets/svg/invoice.svg"
import { Box } from "@mui/material"
import { useQueryClassesStoreTransactions } from "api/reactQuery/queries/classesStoreTransactions"
import { useQueryExportClassStoreTransactionsTask } from "api/reactQuery/queries/export"
import { ExportType } from "api/reactQuery/queries/export.types"
import { AxiosResponse } from "axios"
import DialogUpgradeAccount from "components/common/dialogUpgradeAccount/DialogUpgradeAccount"
import ErrorText from "components/common/error/errorText"
import {
  ExportButton,
  ExportPendingBanner,
  ExportRowToggle,
  InlineExportSubmenu,
} from "components/common/exportMenu"
import NoElementMessageWithoutBtn from "components/common/listing/noElementMessageWithoutBtn"
import NoMatchesForSearch from "components/common/listing/noMatchesForSearch"
import { PopOverStyle } from "components/common/poprow"
import TableSkeleton from "components/common/skeleton/tableSkeleton"
import SearchbarForm from "components/form/searchbarForm"
import { useArchivedClassGuard } from "hooks/archivedClassGuard"
import { useDialog } from "hooks/dialog"
import { useExportGuard, useExportMenu, useExportTasks } from "hooks/exportReports"
import { useGroupStoreExport } from "hooks/groupStoreExport"
import { useSelectedItems, useSorting } from "hooks/listing"
import { useFilterMultipleItems } from "hooks/listing/filtering"
import { useClassId } from "hooks/navigation"
import { useCustomPayment } from "hooks/payment"
import { userIsFreeDemoSchoolPlan } from "hooks/schoolPlans"
import { useCustomSnackbar } from "hooks/snackbar"
import { useAppSelector } from "hooks/store"
import mixpanel from "mixpanel-browser"
import { useTranslation } from "react-i18next"
import { selectUser } from "store/auth/auth.selectors"
import { PrimaryTile } from "styles/common/tile"
import { ITransactionItemClass } from "ts/interfaces/Transaction"
import { getErrorMessageFromTab } from "utils/api"
import { schoolPlanToExportData } from "utils/export"

import BatchActionButtons from "./batchActionButtons"
import GroupsInReportPicker from "./groupsInReportPicker"

interface IProps {
  renderDataView: (data: {
    apiData: AxiosResponse<ITransactionItemClass[], unknown> | undefined
    sortingProps: ReturnType<typeof useSorting<ITransactionItemClass>>
    selectingProps: ReturnType<typeof useSelectedItems<ITransactionItemClass>>
    loading?: boolean
  }) => ReactNode
}

const ViewCommonElementsContainer: FC<IProps> = ({ renderDataView }) => {
  const { t } = useTranslation()
  const { showSnackbar } = useCustomSnackbar()
  const classId = useClassId()
  const userInfo = useAppSelector(selectUser)
  const [isExportData, setIsExportData] = useState(false)
  const [isRowOpenStore, setIsRowOpenStore] = useState(false)
  const [isRowOpenByGroup, setIsRowOpenByGroup] = useState(false)
  const [loader, setLoader] = useState(false)
  const isFreeDemo = userIsFreeDemoSchoolPlan()
  const { isClassLoaded, isArchived } = useArchivedClassGuard()
  const canExportByGroup = !isFreeDemo && isClassLoaded && !isArchived

  const { data, isLoading, isError } = useQueryClassesStoreTransactions({
    classId: Number(classId),
  })

  const { payIt, isLoadingPayments } = useCustomPayment()

  const {
    isOpen: isOpenExport,
    handleOpen: handleOpenExport,
    handleClose: handleCloseExport,
  } = useDialog()

  const {
    successTasks,
    pendingTasks,
    setExportReportIdToDownload,
    handleExportSuccess,
    refreshTasks,
  } = useExportTasks({
    exportType: ExportType.CLASS_STORE_TRANSACTIONS,
    classId: Number(classId),
  })

  const { isFetching, refetch: refetchExportData } =
    useQueryExportClassStoreTransactionsTask({
      classId: Number(classId),
      options: {
        enabled: isExportData,
        onSuccess: (response) => {
          setIsExportData(false)
          handleExportSuccess(response, Number(classId))
          showSnackbar({
            title: t("export.fileIsCurrentlyBeingGenerated"),
          })
        },
        onError: (err) => {
          showSnackbar({
            title: t(getErrorMessageFromTab(err)),
            variant: "error",
          })
        },
      },
    })

  const groupExport = useGroupStoreExport(classId, {
    enabled: canExportByGroup,
  })

  const {
    exportMenuAnchorEl,
    isExportMenuOpen,
    handleExportClick,
    handleExportMenuClose,
    handleGenerateNewFile: handleGenerateFile,
  } = useExportMenu(isFetching || groupExport.isFetching, () => {
    refreshTasks()
    groupExport.refreshTasks()
  })

  const { isGenerateDisabled } = useExportGuard({
    hasPendingTasks: pendingTasks.length > 0,
  })

  const collapseExportRows = () => {
    setIsRowOpenStore(false)
    setIsRowOpenByGroup(false)
  }

  const handleCloseExportMenu = () => {
    handleExportMenuClose()
    collapseExportRows()
    groupExport.reset()
  }

  const { searchFilter, setSearchFilter, filteredMultiData } =
    useFilterMultipleItems({ data: data ? data.data : [] })

  const sortingProps = useSorting({
    data: filteredMultiData,
  })
  const { sortedData } = sortingProps

  const selectingProps = useSelectedItems(sortedData)
  const { selectedItems, clearSelectedItems } = selectingProps

  return (
    <PrimaryTile sx={{ borderRadius: "0px 8px 8px 8px", overflow: "hidden" }}>
      <Box p="16px" pb="24px" display="flex" columnGap={2}>
        <SearchbarForm
          placeholder={`${t("store.searchForTransaction")}...`}
          onSubmit={(data) => setSearchFilter(data.search)}
        />
        <DialogUpgradeAccount
          open={isOpenExport}
          onClose={() => {
            handleCloseExport()
            mixpanel.track("reports_export", {
              method: "class_store_transactions",
              action: "cancel",
            })
          }}
          onActionButtonClick={() => {
            payIt(null)
            mixpanel.track("reports_export", {
              method: "class_store_transactions",
              action: "submit",
            })
          }}
          isLoading={isLoadingPayments}
          isExport
        />
        <ExportButton
          isFetching={isFetching || groupExport.isFetching}
          onClick={handleExportClick}
        />
        <PopOverStyle
          anchorEl={exportMenuAnchorEl}
          open={isExportMenuOpen}
          onClose={handleCloseExportMenu}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          slotProps={{ paper: { sx: { mt: 0.5 } } }}
        >
          <ul style={{ width: "250px" }}>
            <ExportRowToggle
              label={t("store.exportData")}
              isOpen={isRowOpenStore}
              onToggle={() => setIsRowOpenStore((prev) => !prev)}
            />
            <InlineExportSubmenu
              open={isRowOpenStore}
              successTasks={successTasks}
              onGenerateNewFile={() =>
                handleGenerateFile(() => {
                  collapseExportRows()
                  schoolPlanToExportData({
                    user: userInfo,
                    upgrade: handleOpenExport,
                    exportData: refetchExportData,
                  })
                })
              }
              onFileClick={setExportReportIdToDownload}
              wrapFilename
              isGenerateDisabled={isGenerateDisabled}
              generateDisabledTooltip={t("export.reportCurrentlyBeingGenerated")}
            />
            {canExportByGroup && (
              <>
                <ExportRowToggle
                  label={t("group.exportStoreByGroup")}
                  isOpen={isRowOpenByGroup}
                  onToggle={() => setIsRowOpenByGroup((prev) => !prev)}
                />
                {isRowOpenByGroup && groupExport.hasGroups && (
                  <GroupsInReportPicker
                    groups={groupExport.groups}
                    selectedIds={groupExport.selectedIds}
                    onChange={groupExport.setSelectedIds}
                  />
                )}
                <InlineExportSubmenu
                  open={isRowOpenByGroup}
                  successTasks={groupExport.successTasks}
                  onGenerateNewFile={() =>
                    handleGenerateFile(() => {
                      collapseExportRows()
                      groupExport.requestExport()
                    })
                  }
                  onFileClick={groupExport.setExportReportIdToDownload}
                  onFileRemove={groupExport.removeTask}
                  wrapFilename
                  isGenerateDisabled={groupExport.isGenerateDisabled}
                  generateDisabledTooltip={groupExport.generateDisabledTooltip}
                />
              </>
            )}
          </ul>
        </PopOverStyle>
      </Box>
      <BatchActionButtons
        selectedItems={selectedItems}
        clearSelectedItems={clearSelectedItems}
        setLoader={setLoader}
        sortedData={sortedData}
      />

      {renderDataView({
        apiData: data,
        selectingProps,
        sortingProps,
        loading: loader,
      })}
      {isLoading && <TableSkeleton />}
      {isError && <ErrorText />}
      {!isLoading &&
        data?.data &&
        data?.data.length > 0 &&
        sortedData.length === 0 && <NoMatchesForSearch search={searchFilter} />}
      {!isLoading && data?.data?.length === 0 && (
        <NoElementMessageWithoutBtn
          Icon={<SvgInvoice />}
          title={t("store.youDoNotHaveAnyTasksToDo")}
          description={t("store.greatWorkStayingOnTop")}
        />
      )}
      <ExportPendingBanner
        visible={
          pendingTasks.length > 0 || groupExport.pendingTasks.length > 0
        }
      />
    </PrimaryTile>
  )
}

export default ViewCommonElementsContainer
